import React from "react";

// ─── Utility ────────────────────────────────────────────────────────────────

const getInitials = (name = "") =>
    name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);

const AVATAR_GRADIENTS = [
    ["#f953c6", "#b91d73"],
    ["#f7971e", "#ffd200"],
    ["#11998e", "#38ef7d"],
    ["#4facfe", "#00f2fe"],
    ["#a18cd1", "#fbc2eb"],
    ["#f093fb", "#f5576c"],
    ["#43e97b", "#38f9d7"],
    ["#fa709a", "#fee140"],
];

const getGradient = (str = "") => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

const getRoleColor = (role = "") => {
    const map = {
        chairman: { bg: "#e0f7fa", color: "#00796b", border: "#b2dfdb" },
        admin: { bg: "#ede7f6", color: "#6a1b9a", border: "#ce93d8" },
        employee: { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
        manager: { bg: "#fff3e0", color: "#e65100", border: "#ffcc80" },
        human: { bg: "#fce4ec", color: "#c2185b", border: "#f48fb1" },
    };
    return map[role.toLowerCase()] || { bg: "#f5f5f5", color: "#555", border: "#ddd" };
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const Avatar = ({ name, size = 52 }) => {
    const [c1, c2] = getGradient(name);
    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: 14,
                background: `linear-gradient(135deg, ${c1}, ${c2})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: size * 0.32,
                color: "#fff",
                letterSpacing: 1,
                flexShrink: 0,
                boxShadow: `0 4px 14px ${c1}55`,
            }}
        >
            {getInitials(name)}
        </div>
    );
};

const RoleBadge = ({ role }) => {
    const s = getRoleColor(role);
    return (
        <span
            style={{
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                borderRadius: 20,
                padding: "3px 11px",
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif",
                whiteSpace: "nowrap",
            }}
        >
            {role}
        </span>
    );
};

const UserCard = ({ userEntry }) => {
    const { user, rolOrgSpicificShortName, deptOrgSpicificShortName } = userEntry;
    const fullName = user
        ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.loginName || "Unknown"
        : "Unknown";
    const email = user?.email || "";
    const memberNo = (user.apikey1 || user.apikey2)
        ? `${user.apikey1 || ""}${user.apikey2 || ""}`
        : "";

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                background: "#fff",
                borderRadius: 16,
                padding: "18px 22px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                transition: "transform 0.18s ease, box-shadow 0.18s ease",
                cursor: "default",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.10)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)";
            }}
        >
            <Avatar name={fullName} size={52} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
                    <span
                        style={{
                            fontWeight: 700,
                            fontSize: 15,
                            color: "#1a1a2e",
                            fontFamily: "'DM Sans', sans-serif",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {fullName}
                    </span>
                    {deptOrgSpicificShortName && deptOrgSpicificShortName !== rolOrgSpicificShortName && (
                        <RoleBadge role={deptOrgSpicificShortName} />
                    )}
                </div>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px 20px",
                        fontSize: 13,
                        color: "#6b7280",
                        fontFamily: "'DM Sans', sans-serif",
                    }}
                >
                    {/* ✅ Show email */}
                    {email && (
                        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 14 }}>✉</span> {email}
                        </span>
                    )}
                    {/* ✅ Show Member No if available, hide phone & address */}
                    {memberNo && (
                        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            {memberNo}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};
const GroupSection = ({ groupName, members }) => {
    const [c1] = getGradient(groupName);
    return (
        <div style={{ marginBottom: 32 }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 16,
                }}
            >
                <div
                    style={{
                        width: 4,
                        height: 28,
                        borderRadius: 4,
                        background: `linear-gradient(180deg, ${c1}, ${getGradient(groupName)[1]})`,
                    }}
                />
                <h3
                    style={{
                        margin: 0,
                        fontSize: 15,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1.5,
                        color: "#1a1a2e",
                        fontFamily: "'DM Sans', sans-serif",
                    }}
                >
                    {groupName}
                </h3>
                {/*<span*/}
                {/*    style={{*/}
                {/*        background: "#f3f4f6",*/}
                {/*        borderRadius: 20,*/}
                {/*        padding: "2px 10px",*/}
                {/*        fontSize: 12,*/}
                {/*        color: "#6b7280",*/}
                {/*        fontWeight: 600,*/}
                {/*        fontFamily: "'DM Sans', sans-serif",*/}
                {/*    }}*/}
                {/*>*/}
                {/*    {members.length}*/}
                {/*</span>*/}
            </div>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                    gap: 12,
                }}
            >
                {members.map((m, i) => (
                    <UserCard key={m.userId || i} userEntry={m} />
                ))}
            </div>
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function UserDirectory({ userData }) {
    // Filter users where url is truthy
    const filtered = (userData?.users || []).filter((u) => u.url === "true");

    // Group by rolOrgSpicificShortName
    const groups = filtered.reduce((acc, user) => {
        const key = user.biotype || "Others"; // ✅ change here
        if (!acc[key]) acc[key] = [];
        acc[key].push(user);
        return acc;
    }, {});

    if (filtered.length === 0) {
        return (
            null

        );
    }

    return (
        <div
            style={{
                maxWidth: 900,
                margin: "0 auto",
                padding: "40px 20px",
                fontFamily: "'DM Sans', sans-serif",
            }}
        >
            <div style={{ marginBottom: 36 }}>
                <h2
                    style={{
                        margin: 0,
                        fontSize: 26,
                        fontWeight: 800,
                        color: "#1a1a2e",
                        letterSpacing: -0.5,
                    }}
                >
                    Members
                </h2>
                {/*<p style={{ margin: "6px 0 0", color: "#6b7280", fontSize: 14 }}>*/}
                {/*    {filtered.length} member{filtered.length !== 1 ? "s" : ""}*/}
                {/*</p>*/}
            </div>
            {Object.entries(groups).map(([groupName, members]) => (
                <GroupSection key={groupName} groupName={groupName} members={members} />
            ))}
        </div>
    );
}